import { type ReactNode } from "react"
import { ChevronDown, ChevronUp } from "lucide-react"
import { decorateAssistantTabs } from "@/components/ui/assistant-tab-icon"
import { FloatingTabPill, type FloatingTabPillTab } from "@/components/ui/floating-tab-pill"
import { useTranslation } from "@/hooks/use-translation"
import { cn } from "@/lib/utils"

const TOOLBAR_BUTTON_OPAQUE_CLASS =
  "[&_button.border]:bg-ui-bg-elevated dark:[&_button.border]:hover:bg-zinc-800 light:[&_button.border]:hover:bg-clay-2"

type WorkspaceBottomPanelChromeProps = {
  tabs: FloatingTabPillTab[]
  collapsed: boolean
  onCollapsedChange: (collapsed: boolean) => void
  /** Right-aligned actions above the panel card (same placement as shots agent toolbar). */
  toolbar?: ReactNode
  dirtyAriaLabel?: string
  /** Agent run is streaming: Assistant tab shows the spinner instead of Sparkles. */
  assistantBusy?: boolean
  className?: string
  children: ReactNode
}

/**
 * Shared inner chrome for canvas generation panel and entity assistant dock:
 * centered tab pill, optional toolbar above the card, and chevron collapse — same as canvas.
 */
export function WorkspaceBottomPanelChrome({
  tabs,
  collapsed,
  onCollapsedChange,
  toolbar,
  dirtyAriaLabel,
  assistantBusy = false,
  className,
  children,
}: WorkspaceBottomPanelChromeProps) {
  const { t } = useTranslation("workspace")
  const toggleCollapsed = () => onCollapsedChange(!collapsed)
  const toggleLabel = collapsed ? t("canvas.panel.expand") : t("canvas.panel.collapse")

  return (
    <div className={cn("pointer-events-none relative flex w-full min-w-0 flex-col gap-2", className)}>
      {toolbar ? (
        <div className="pointer-events-none flex flex-wrap items-center justify-end gap-2">
          <div className={cn("pointer-events-auto flex flex-wrap items-center gap-2", TOOLBAR_BUTTON_OPAQUE_CLASS)}>
            {toolbar}
          </div>
        </div>
      ) : null}

      <div className="pointer-events-auto relative w-full min-w-0">
        {tabs.length >= 2 ? (
          <div
            className="pointer-events-auto absolute top-0 left-1/2 z-50 -translate-x-1/2 -translate-y-1/2"
            data-workspace-dock-chrome
          >
            <FloatingTabPill dirtyAriaLabel={dirtyAriaLabel} tabs={decorateAssistantTabs(tabs, assistantBusy)} />
          </div>
        ) : null}

        <button
          type="button"
          onClick={toggleCollapsed}
          data-workspace-dock-chrome
          className="absolute top-[2px] right-2 z-50 cursor-pointer rounded-lg p-1 text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary"
          title={toggleLabel}
          aria-label={toggleLabel}
        >
          {collapsed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>

        {children}
      </div>
    </div>
  )
}
